import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'
import { calculateBoundingBox, type Coordinate } from '@limo/shared/pricing'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

type FlatRateZone = Tables<'flat_rate_zones'>

// Preset colors for zone selection
const ZONE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
]

const zoneSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format'),
  is_active: z.boolean().default(true),
})

type ZoneFormData = z.infer<typeof zoneSchema>

interface ZoneDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  zone: FlatRateZone | null
  pendingPolygon: Coordinate[] | null
  accountId: string
}

export function ZoneDialog({
  open,
  onOpenChange,
  zone,
  pendingPolygon,
  accountId,
}: ZoneDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!zone

  const form = useForm<ZoneFormData>({
    resolver: zodResolver(zoneSchema),
    defaultValues: {
      name: '',
      description: '',
      color: ZONE_COLORS[0],
      is_active: true,
    },
  })

  // Reset form when dialog opens with zone data
  useEffect(() => {
    if (open) {
      if (zone) {
        form.reset({
          name: zone.name,
          description: zone.description || '',
          color: zone.color,
          is_active: zone.is_active,
        })
      } else {
        // New zone - use a random color
        const randomColor = ZONE_COLORS[Math.floor(Math.random() * ZONE_COLORS.length)]
        form.reset({
          name: '',
          description: '',
          color: randomColor,
          is_active: true,
        })
      }
    }
  }, [open, zone, form])

  // Create zone mutation
  const createMutation = useMutation({
    mutationFn: async (data: ZoneFormData) => {
      if (!pendingPolygon) {
        throw new Error('No polygon data available')
      }

      const boundingBox = calculateBoundingBox(pendingPolygon)

      const { error } = await supabase.from('flat_rate_zones').insert({
        account_id: accountId,
        name: data.name,
        description: data.description || null,
        color: data.color,
        is_active: data.is_active,
        polygon: pendingPolygon,
        bounding_box: boundingBox,
      })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flat-rate-zones', accountId] })
      toast({
        title: 'Zone created',
        description: 'The zone has been created successfully.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Update zone mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ZoneFormData) => {
      if (!zone) throw new Error('No zone to update')

      const { error } = await supabase
        .from('flat_rate_zones')
        .update({
          name: data.name,
          description: data.description || null,
          color: data.color,
          is_active: data.is_active,
        })
        .eq('id', zone.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flat-rate-zones', accountId] })
      toast({
        title: 'Zone updated',
        description: 'The zone has been updated successfully.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: ZoneFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Zone' : 'Create Zone'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the zone details. The zone boundaries can be edited on the map.'
              : 'Give your new zone a name and select a color.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Downtown, Airport" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the geographic area covered by this zone..."
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex gap-2 flex-wrap">
                      {ZONE_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-transform ${
                            field.value === color
                              ? 'border-foreground scale-110'
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                        />
                      ))}
                    </div>
                  </FormControl>
                  <FormDescription>
                    Choose a color to identify this zone on the map
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive zones won't be used for flat rate matching
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Zone'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
